/* journeyLayout — pure layout engine for JourneyFlowGraph: journey doc →
 * positioned nodes + raw edges. Ranking is BFS depth from the entry surface
 * (first in the list); order within a rank is tidied with a barycenter pass
 * to reduce edge crossings. Edge geometry lives in edgeRouting.ts so the
 * graph can re-route when nodes are dragged. */

export type NodeKind = 'screen' | 'router' | 'task' | 'agent' | 'journey' | 'end';

export interface GNode {
    key: string;
    kind: NodeKind;
    title: string;
    sub: string;
    entry: boolean;
    layer: number;
    x: number;
    y: number;
    w: number;
    h: number;
    tipTitle: string;
    tipBody: string;
}

export interface RawEdge {
    from: string;
    to: string;
    when?: string;
    transition?: any;
    /** Workflow-graph extras — journey edges leave these unset and keep
     * the when-derived label + tooltip computed in edgeRouting. */
    label?: string;
    kind?: string;
    tipTitle?: string;
    tipBody?: string;
}

export interface GraphBase {
    nodes: GNode[];
    raw: RawEdge[];
    problems: string[];
}

const SURF_W = 200;
const SURF_H = 60;
const JOUR_H = 48;
const END_W = 110;
const END_H = 36;
const COL_W = SURF_W + 96;
const ROW_GAP = 34;
const PAD_X = 28;
const PAD_TOP = 44;

export const END_KEY = '__end__';

export function surfaceKind(s: any): NodeKind {
    const mode = s?.mode || {};
    if (mode.type === 'task' || s?.task) return 'task';
    if (mode.type === 'agent') return 'agent';
    if (s?.content != null) return 'screen';
    return 'router';
}

function nodeTip(s: any, kind: NodeKind): string {
    const lines: string[] = [`id: ${s.id} · ${kind}`];
    const reads = Array.isArray(s.reads)
        ? s.reads.map((r: any) => r?.workflow).filter(Boolean) : [];
    if (reads.length) lines.push(`reads: ${reads.join(', ')}`);
    const actions = s.actions && typeof s.actions === 'object'
        ? Object.keys(s.actions) : [];
    if (actions.length) lines.push(`actions: ${actions.join(', ')}`);
    const exits = Array.isArray(s.exits) ? s.exits.length : 0;
    lines.push(`exits: ${exits}`);
    return lines.join('\n');
}

/** Collect drawable edges; push anything off-shape into problems. */
function collectEdges(surfaces: any[], surfIds: Set<string>, problems: string[]): {
    raw: RawEdge[];
    journeyTargets: string[];
    hasEnd: boolean;
} {
    const raw: RawEdge[] = [];
    const journeyTargets: string[] = [];
    let hasEnd = false;
    for (const s of surfaces) {
        if (!s?.id) {
            problems.push('surface without an id — skipped');
            continue;
        }
        const exits = s.exits;
        if (exits != null && !Array.isArray(exits)) {
            problems.push(`${s.id}: exits is not a list — skipped`);
            continue;
        }
        for (const e of exits || []) {
            const tr = e?.transition || e || {};
            const to = tr.to;
            if (to === 'end') {
                hasEnd = true;
                raw.push({
                    from: s.id,
                    to: END_KEY,
                    when: e?.when,
                    transition: tr
                });
            } else if (to === 'journey') {
                if (!tr.target) {
                    problems.push(`${s.id}: journey exit without target — skipped`);
                    continue;
                }
                if (!journeyTargets.includes(tr.target)) journeyTargets.push(tr.target);
                raw.push({
                    from: s.id,
                    to: `journey:${tr.target}`,
                    when: e?.when,
                    transition: tr
                });
            } else if (to === 'stage') {
                if (!surfIds.has(tr.target)) {
                    problems.push(`${s.id}: exit to unknown surface `
                        + `"${tr.target}" — skipped`);
                    continue;
                }
                raw.push({
                    from: s.id,
                    to: tr.target,
                    when: e?.when,
                    transition: tr
                });
            } else {
                problems.push(`${s.id}: exit with unknown transition.to `
                    + `"${String(to)}" — skipped`);
            }
        }
    }
    return {
        raw,
        journeyTargets,
        hasEnd
    };
}

/** BFS ranks: shortest hop-distance from the entry surface; surfaces the
 * entry never reaches seed new BFS roots at rank 0, in list order. */
function bfsLayers(surfaces: any[], surfIds: Set<string>, raw: RawEdge[]): Map<string, number> {
    const nexts = new Map<string, string[]>();
    for (const e of raw) {
        if (surfIds.has(e.to) && e.to !== e.from) {
            nexts.set(e.from, [...(nexts.get(e.from) || []), e.to]);
        }
    }
    const layer = new Map<string, number>();
    const queue: string[] = [];
    let qi = 0;
    const seed = (id: string) => {
        if (layer.has(id)) return;
        layer.set(id, 0);
        queue.push(id);
        while (qi < queue.length) {
            const cur = queue[qi++];
            const l = layer.get(cur) ?? 0;
            for (const t of nexts.get(cur) || []) {
                if (!layer.has(t)) {
                    layer.set(t, l + 1);
                    queue.push(t);
                }
            }
        }
    };
    if (surfaces[0]?.id) seed(surfaces[0].id);
    for (const s of surfaces) {
        if (s?.id) seed(s.id);
    }
    return layer;
}

/** Barycenter ordering inside each rank — three alternating sweeps pulling
 * every node toward the mean position of its neighbors one rank over. */
function orderByBarycenter(byLayer: Map<number, GNode[]>, raw: RawEdge[],
    layerOf: Map<string, number>): void {
    const layers = Array.from(byLayer.keys()).sort((a, b) => a - b);
    const nbrs = new Map<string, string[]>();
    for (const e of raw) {
        if (e.from === e.to) continue;
        nbrs.set(e.from, [...(nbrs.get(e.from) || []), e.to]);
        nbrs.set(e.to, [...(nbrs.get(e.to) || []), e.from]);
    }
    const idx = new Map<string, number>();
    for (const l of layers) {
        (byLayer.get(l) || []).forEach((n, i) => idx.set(n.key, i));
    }
    const sweep = (dir: 1 | -1) => {
        const seq = dir === 1 ? layers : [...layers].reverse();
        for (const l of seq) {
            const col = byLayer.get(l) || [];
            const bary = (n: GNode): number => {
                const ns = (nbrs.get(n.key) || [])
                    .filter((k) => layerOf.get(k) === l - dir);
                if (ns.length === 0) return idx.get(n.key) ?? 0;
                return ns.reduce((a, k) => a + (idx.get(k) ?? 0), 0) / ns.length;
            };
            col.sort((a, b) => bary(a) - bary(b));
            col.forEach((n, i) => idx.set(n.key, i));
        }
    };
    sweep(1);
    sweep(-1);
    sweep(1);
}

export function buildGraph(doc: any): GraphBase {
    const problems: string[] = [];
    const surfaces: any[] = Array.isArray(doc?.surfaces) ? doc.surfaces : [];
    const surfIds = new Set(surfaces.map((s) => s?.id).filter(Boolean));
    const {
        raw,
        journeyTargets,
        hasEnd
    } = collectEdges(surfaces, surfIds, problems);

    const layer = bfsLayers(surfaces, surfIds, raw);

    // Terminals sit one layer past their deepest source.
    const terminalLayer = (key: string): number => {
        let l = 1;
        for (const e of raw) {
            if (e.to === key) l = Math.max(l, (layer.get(e.from) ?? 0) + 1);
        }
        return l;
    };

    const nodes: GNode[] = [];
    const entryId = surfaces[0]?.id;
    for (const s of surfaces) {
        if (!s?.id) continue;
        const kind = surfaceKind(s);
        nodes.push({
            key: s.id,
            kind,
            title: s.title || s.name || s.id,
            sub: `${s.id} · ${kind}`,
            entry: s.id === entryId,
            layer: layer.get(s.id) ?? 0,
            x: 0,
            y: 0,
            w: SURF_W,
            h: SURF_H,
            tipTitle: s.title || s.id,
            tipBody: nodeTip(s, kind)
        });
    }
    for (const t of journeyTargets) {
        nodes.push({
            key: `journey:${t}`,
            kind: 'journey',
            title: t,
            sub: 'journey handoff',
            entry: false,
            layer: terminalLayer(`journey:${t}`),
            x: 0,
            y: 0,
            w: SURF_W,
            h: JOUR_H,
            tipTitle: `journey: ${t}`,
            tipBody: 'Hands the run off to this journey.'
        });
    }
    if (hasEnd) {
        nodes.push({
            key: END_KEY,
            kind: 'end',
            title: 'END',
            sub: '',
            entry: false,
            layer: terminalLayer(END_KEY),
            x: 0,
            y: 0,
            w: END_W,
            h: END_H,
            tipTitle: 'END',
            tipBody: 'The journey completes here.'
        });
    }

    const byLayer = new Map<number, GNode[]>();
    for (const n of nodes) {
        byLayer.set(n.layer, [...(byLayer.get(n.layer) || []), n]);
    }
    const layerOf = new Map(nodes.map((n) => [n.key, n.layer]));
    orderByBarycenter(byLayer, raw, layerOf);

    const layers = Array.from(byLayer.keys()).sort((a, b) => a - b);
    const rowsMax = Math.max(1,
        ...Array.from(byLayer.values()).map((l) => l.length));
    const gridH = rowsMax * (SURF_H + ROW_GAP) - ROW_GAP;
    for (const l of layers) {
        const col = byLayer.get(l) || [];
        const colH = col.reduce((a, n) => a + n.h, 0) + (col.length - 1) * ROW_GAP;
        let y = PAD_TOP + Math.max(0, (gridH - colH) / 2);
        for (const n of col) {
            n.x = PAD_X + l * COL_W + (SURF_W - n.w) / 2;
            n.y = y;
            y += n.h + ROW_GAP;
        }
    }

    return {
        nodes,
        raw,
        problems
    };
}
